/*************************************************************************
 *  ADOBE CONFIDENTIAL
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 *  NOTICE:  All information contained herein is, and remains
 *  the property of Adobe and its suppliers, if any. The intellectual
 *  and technical concepts contained herein are proprietary to Adobe
 *  and its suppliers and are protected by all applicable intellectual
 *  property laws, including trade secret and copyright laws.
 *  Dissemination of this information or reproduction of this material
 *  is strictly forbidden unless prior written permission is obtained
 *  from Adobe.
 **************************************************************************/

import type { RootState } from '../../../@types';
import { updatePreSearchRecommendations } from '../../../actions/searchActions';
import React, { FC, ReactElement, useEffect, useState } from 'react';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import { ConnectedProps, connect } from 'react-redux';
import SearchDropDownQuickLinksItem from './SearchDropDownQuickLinksItem';
import SearchHostContext from '../../../utils/SearchHostContext';

import SearchAnalytics from '../../../analytics/SearchAnalytics';
import HorizontalRuler from '../../HorizontalRuler';
import { PRESEARCH_UI_SORT_ORDERS } from '../../../containers/SearchResultsPage/SearchConstants';

type OwnProps = Record<string, any>;

type SearchDropDownQuickLinksProps = OwnProps & WrappedComponentProps & PropsFromRedux;

const SearchDropDownQuickLinks: FC<SearchDropDownQuickLinksProps> = ({
	searchConfig,
	recommendationsGroups,
	searchAppMetadataList,
	updatePreSearchRecommendations,
}): ReactElement => {
	const searchHostContext = SearchHostContext.getContext();
	const analytics = new SearchAnalytics(searchHostContext);

	const [quickLinksData, setQuickLinksData] = useState<{ groupText: string; recommendations: Array<any> }>({
		groupText: '',
		recommendations: [],
	});

	useEffect(() => {
		const recommendationsFromConfig = searchConfig?.ui?.preSearchRecommendations;
		recommendationsFromConfig &&
			searchAppMetadataList &&
			!(recommendationsGroups && Object.keys(recommendationsGroups).length) &&
			updatePreSearchRecommendations(recommendationsFromConfig);
	}, [searchConfig, recommendationsGroups, searchAppMetadataList, updatePreSearchRecommendations]);

	useEffect(() => {
		let quickLinksGroupData: { groupText: string; recommendations: Array<any> } = {
			groupText: '',
			recommendations: [],
		};
		Object.entries(recommendationsGroups).forEach(([groupType, recommendations]) => {
			const groupTypeLowerCase = groupType.toLowerCase();
			if (groupTypeLowerCase === 'quick links') {
				quickLinksGroupData = {
					groupText: 'app.search.presearchRecommendation.group.quicklinks',
					recommendations,
				};
			}
		});
		setQuickLinksData(quickLinksGroupData);
	}, [recommendationsGroups, searchAppMetadataList]);

	useEffect(() => {
		if (quickLinksData.recommendations.length) {
			analytics.postPreSearchItemAction({
				recommendationType: 'quickLinks',
				eventType: 'render',
				uiSortOrder: PRESEARCH_UI_SORT_ORDERS.quickLinks,
				data: quickLinksData.recommendations,
			});
		}
	}, [quickLinksData]);

	if (
		!quickLinksData.groupText ||
		!(Array.isArray(quickLinksData.recommendations) && quickLinksData.recommendations.length)
	) {
		return null;
	}

	return (
		<div className='search-dropdown-quick-links'>
			<HorizontalRuler />
			<div className='search-dropdown-quick-links-group-container'>
				<div key={'preSearch_quickLinks'} className='search-dropdown-quick-links-group'>
					{quickLinksData.recommendations.map((recommendation, index) => (
						<SearchDropDownQuickLinksItem
							key={recommendation.id}
							data={recommendation}
							index={index}
							type='quickLinks'
							count={quickLinksData.recommendations.length}
						/>
					))}
					<HorizontalRuler />
				</div>
			</div>
		</div>
	);
};

const mapStateToProps = ({ search }: RootState) => ({
	searchConfig: search.searchConfig,
	recommendationsGroups: search.preSearchRecommendations,
	searchAppMetadataList: search.searchAppMetadataList,
});

const mapDispatchToProps = {
	updatePreSearchRecommendations,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(SearchDropDownQuickLinks));
